import { Record } from "immutable";
import { BulkContractEnrollmentSummary } from "models/interfaces/enrollments/bulk-contract-enrollment-summary";
import { t } from "utilities/localization/t";
import { BulkEnrollmentSummaryRecord } from "./bulk-enrollment-summary-record";

// -------------------------------------------------------------------------------------------------
// #region Default Values
// -------------------------------------------------------------------------------------------------

const defaultValues: BulkContractEnrollmentSummary = {
    existing: [],
    failed: [],
    successful: [],
    exceededLimit: [],
    csvUploadResults: "",
};

// #endregion Default Values

// -------------------------------------------------------------------------------------------------
// #region Record
// -------------------------------------------------------------------------------------------------

class BulkContractEnrollmentSummaryRecord
    extends Record(defaultValues)
    implements BulkContractEnrollmentSummary
{
    // -------------------------------------------------------------------------------------------------
    // #region Private Members
    // -------------------------------------------------------------------------------------------------

    private readonly _bulkEnrollmentSummaryRecord: BulkEnrollmentSummaryRecord;

    // #endregion Private Members

    // -------------------------------------------------------------------------------------------------
    // #region Constructor
    // -------------------------------------------------------------------------------------------------

    constructor(params?: Partial<BulkContractEnrollmentSummary>) {
        params = params ?? Object.assign({}, defaultValues);

        super(params);

        this._bulkEnrollmentSummaryRecord = new BulkEnrollmentSummaryRecord(params);
    }

    // #endregion Constructor

    // -------------------------------------------------------------------------------------------------
    // #region Public Methods
    // -------------------------------------------------------------------------------------------------

    /**
     * Convenience method to get the total bulk enrollment records calculated from the resultObject successful, existing and failed properties length.
     *
     * @returns {number} total bulk enrollment records calculated from the resultObject successful, existing and failed properties length.
     */
    public calculateTotalBulkContractEnrollmentRecords(): number {
        return (
            this._bulkEnrollmentSummaryRecord.calculateTotalBulkEnrollmentRecords() +
            this.exceededLimit.length
        );
    }

    /**
     * Convenience method to a summary details object for the bulk contract enrollment summary.
     *
     * @returns {Array<{ name: string; value: any }>} An array of name/value pairs for the summary details.
     */
    public getSummaryDetails(): { name: string; value: any; description?: string }[] {
        return [
            {
                name: t("totalImported"),
                value: this.calculateTotalBulkContractEnrollmentRecords(),
            },
            {
                name: t("successfullyEnrolled"),
                value: this.successful.length,
                description: t(
                    "learnersInThisGroupWereSuccessfullyAddedToTheEnrollmentListForTheTraining"
                ),
            },
            {
                name: t("alreadyEnrolled"),
                value: this.existing.length,
                description: t(
                    "learnersInThisGroupMatchedRecordsForLearnersAlreadyOnTheEnrollmentListForTheTraining"
                ),
            },
            {
                name: t("failedToEnroll"),
                value: this.failed.length,
                description: t(
                    "learnersInThisGroupWereNotAddedToTheEnrollmentListForThisTrainingDueToAPotentialDataError"
                ),
            },
            {
                name: t("exceededEnrollmentLimit"),
                value: this.exceededLimit.length,
                description: t(
                    "learnersInThisGroupWereNotAddedToTheEnrollmentListDueToExceedingTheRegistrationLimitForThisTraining"
                ),
            },
        ];
    }

    /**
     * Given a set of values for BulkContractEnrollmentSummary properties, create a new
     * BulkContractEnrollmentSummaryRecord object from this instance, overwriting the properties in the
     * values parameter with values provided.
     *
     * @param {Partial<BulkContractEnrollmentSummary>} values The values to overwrite on this instance.
     * @returns A BulkContractEnrollmentSummaryRecord with values from this instance and the values parameter.
     */
    public with(
        values: Partial<BulkContractEnrollmentSummary>
    ): BulkContractEnrollmentSummaryRecord {
        return new BulkContractEnrollmentSummaryRecord(Object.assign(this.toJS(), values));
    }

    // #endregion Public Methods
}

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { BulkContractEnrollmentSummaryRecord };

// #endregion Exports
